// Sidebar.jsx
import React from "react";
import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Tooltip
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import ExtensionIcon from "@mui/icons-material/Extension";
import BarChartIcon from "@mui/icons-material/BarChart";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SettingsIcon from "@mui/icons-material/Settings";
import LinkIcon from '@mui/icons-material/Link';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';

import { getCurrentUser, hasAnyRole } from "../../utils/auth";

// default widths (also used in Dashboard)
const EXPANDED_WIDTH = 280;
const COLLAPSED_WIDTH = 72;

export default function Sidebar({
                                    mobileOpen,
                                    onClose,
                                    expanded,
                                    onToggleExpanded,
                                    collapsedWidth = COLLAPSED_WIDTH,
                                    expandedWidth = EXPANDED_WIDTH,
                                }) {
    const location = useLocation();
    const user = getCurrentUser();

    const items = [
        {
            label: "Dashboard",
            path: "/dashboard",
            icon: <DashboardIcon />,
            requiredRoles: [],
            selected:
                location.pathname === "/dashboard" ||
                location.pathname === "/dashboard/"
        },
                {
            label: "CMS-Search",
            path: "/dashboard/cms-search",
            icon: <ManageSearchIcon />,
            requiredRoles: ["admin"],
            selected: location.pathname.startsWith("/dashboard/cms-search")
        },
        {
            label: "Statistiken",
            path: "/dashboard/statistics",
            icon: <BarChartIcon />,
            requiredRoles: ["admin"],
            selected: location.pathname.startsWith("/dashboard/statistics")
        },
        {
            label: "Excluded Domains",
            path: "/dashboard/blocked-domains",
            icon: <PublicOffIcon />,
            requiredRoles: ["admin"],
            selected: location.pathname.startsWith("/dashboard/blocked-domains")
        },
        {
            label: "Link Verzeichnisse",
            path: "/dashboard/linklists",
            icon: <LinkIcon />,
            requiredRoles: ["admin"],
            selected: location.pathname.startsWith("/dashboard/linklists")
        },
        {
            label: "Nutzerverwaltung",
            path: "/dashboard/users",
            icon: <PeopleIcon />,
            requiredRoles: ["admin"],
            selected: location.pathname.startsWith("/dashboard/users")
        },
        {
            label: "Companies",
            path: "/dashboard/companies",
            icon: <ApartmentIcon />,
            requiredRoles: ["admin"],
            selected: location.pathname.startsWith("/dashboard/companies")
        }
        ,
        {
            label: "Settings",
            path: "/dashboard/settings",
            icon: <SettingsIcon />,
            requiredRoles: ["admin"],
            selected: location.pathname.startsWith("/dashboard/settings")
        }
    ];

    const visibleItems = items.filter(i => hasAnyRole(user, i.requiredRoles));
    const desktopWidth = expanded ? expandedWidth : collapsedWidth;

    const content = (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <List sx={{ p: 1 }}>
                {visibleItems.map(item => (
                    <ListItem key={item.path} disablePadding sx={{ display: "block" }}>
                        <Tooltip
                            title={item.label}
                            placement="right"
                            arrow
                            disableHoverListener={expanded} // only when collapsed
                        >
                            <ListItemButton
                                component={RouterLink}
                                to={item.path}
                                selected={item.selected}
                                onClick={onClose}
                                sx={{
                                    borderRadius: 2,
                                    minHeight: 48,
                                    justifyContent: expanded ? "flex-start" : "center",
                                    px: expanded ? 2 : 1,
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: expanded ? 2 : 0,
                                        justifyContent: "center"
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                {expanded && (
                                    <ListItemText
                                        primary={item.label}
                                        sx={{ opacity: 1 }}
                                    />
                                )}
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                ))}
            </List>

            <Box sx={{ flexGrow: 1 }} />

            <Box
                sx={{
                    p: 1.5,
                    pb: 2,
                    display: { xs: "none", md: "flex" },
                    justifyContent: expanded ? "flex-end" : "center",
                }}
            >
                <Tooltip
                    title={expanded ? "Sidebar einklappen" : "Sidebar ausklappen"}
                    placement="right"
                    arrow
                >
                    <IconButton
                        onClick={onToggleExpanded}
                        size="small"
                        sx={{
                            borderRadius: 2,
                            border: "1px solid rgba(148,163,184,0.5)"
                        }}
                    >
                        {expanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );

    return (
        <Box
            component="nav"
            sx={{
                width: { md: desktopWidth },
                flexShrink: { md: 0 }
            }}
            aria-label="sidebar"
        >
            {/* Mobile drawer: always expanded width */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onClose}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: "block", md: "none" },
                    "& .MuiDrawer-paper": {
                        boxSizing: "border-box",
                        width: expandedWidth
                    }
                }}
            >
                {content}
            </Drawer>

            {/* Desktop drawer: collapsible */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: "none", md: "block" },
                    "& .MuiDrawer-paper": {
                        boxSizing: "border-box",
                        width: desktopWidth,
                        borderRight: "1px solid rgba(2,14,41,.06)",
                        overflowX: "hidden"
                    }
                }}
                open
            >
                {content}
            </Drawer>
        </Box>
    );
}
